import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, FileText, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { NDA_QUERY_KEY, useAcceptNda, useNda } from "@/lib/auth";

export function NdaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: nda, isLoading, isError, error, refetch } = useNda();
  const acceptNda = useAcceptNda();

  const [leido, setLeido] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function onAceptar() {
    if (!nda) {
      return;
    }
    setAviso(null);
    setFormError(null);
    try {
      // Se confirma exactamente la versión mostrada (aceptación informada).
      await acceptNda.mutateAsync(nda.version);
      await navigate({ to: "/" });
    } catch (err) {
      if (err instanceof ApiError && err.code === "NDA_VERSION_DESFASADA") {
        // Un admin publicó otra versión mientras se leía: re-pedir y re-mostrar.
        setLeido(false);
        setAviso("La versión del NDA cambió. Vuelve a leer el documento vigente y acéptalo.");
        await queryClient.invalidateQueries({ queryKey: NDA_QUERY_KEY });
        return;
      }
      setFormError(
        err instanceof ApiError
          ? err.message || "No se pudo registrar la aceptación."
          : "No se pudo registrar la aceptación. Comprueba tu conexión.",
      );
    }
  }

  return (
    <main className="flex min-h-dvh flex-col items-center bg-slate-100 px-4 py-8">
      <div className="flex w-full max-w-2xl flex-col gap-4">
        <header className="flex items-center gap-2 text-slate-900">
          <FileText className="h-7 w-7 text-primary-600" aria-hidden="true" />
          <h1 className="text-2xl font-bold tracking-tight">Acuerdo de confidencialidad</h1>
        </header>
        <p className="text-base text-slate-600">
          Lee y acepta los términos vigentes para poder continuar.
        </p>

        {aviso ? (
          <Alert variant="info">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{aviso}</span>
          </Alert>
        ) : null}

        {formError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{formError}</span>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-10 text-slate-500">
            <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
            <span>Cargando documento…</span>
          </div>
        ) : null}

        {isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div className="flex flex-col gap-3">
              <span>
                {error instanceof ApiError
                  ? error.message || "No se pudo cargar el documento."
                  : "No se pudo cargar el documento."}
              </span>
              <Button variant="outline" onClick={() => void refetch()} className="self-start">
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : null}

        {nda ? (
          <>
            <article className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-slate-200 border-b px-5 py-3">
                <span className="font-semibold text-slate-700">Documento</span>
                <span className="text-slate-500 text-sm">Versión {nda.version}</span>
              </div>
              <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap px-5 py-4 text-base text-slate-800 leading-relaxed">
                {nda.texto}
              </div>
            </article>

            <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4">
              <input
                type="checkbox"
                checked={leido}
                onChange={(e) => setLeido(e.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
              />
              <span className="text-base text-slate-800">
                He leído y acepto los términos del acuerdo de confidencialidad (versión{" "}
                {nda.version}
                ).
              </span>
            </label>

            <Button
              type="button"
              size="lg"
              disabled={!leido || acceptNda.isPending}
              onClick={() => void onAceptar()}
              className="w-full"
            >
              {acceptNda.isPending ? (
                <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
              )}
              Aceptar y continuar
            </Button>
          </>
        ) : null}
      </div>
    </main>
  );
}
